import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload from 'Components/FileUpload';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
// Material UI
import { Grid, Alert } from '@mui/material';
// Components
import { Button, Form, Select } from 'Components';
import { useFileUpload } from 'Core/hooks';
import { NotificationManager } from 'Components/Notifications';

// Actions
import {
    importTags,
    openImportTagsModal,
    resetImportStateTag,
    clearErrorTag,
    setFileKeyTag,
    setDelimiterTag
} from './redux/slice';

const ModalImportTags = () => {
    const { messages } = useIntl();
    
    const delimiters = [
        { id: ',', name: messages['tags_comma'] || 'Comma (,)' },
        { id: ';', name: messages['tags_semicolon'] || 'Semicolon (;)' }
    ];
    
    const { 
        openImport, 
        loading, 
        importResponse, 
        error 
    } = useSelector((state) => state.tagsImport);
    
    const dispatch = useDispatch();
    const formRef = useRef();
    const filesUpload = useFileUpload([]);
    
    const [delimiter, setDelimiterLocal] = useState({
        id: '',
        name: messages['tags_selectDelimiter'] || 'Select'
    });
    const [disableButton, setDisableButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: '',
        type: ''
    });

    const handleClose = useCallback(() => {
        dispatch(openImportTagsModal(false));
        dispatch(resetImportStateTag());
        setErrorMessage({
            message: '',
            type: ''
        });
        setDisableButton(false);
        setDelimiterLocal({
            id: '',
            name: messages['tags_selectDelimiter'] || 'Select'
        });
    }, [dispatch, messages]);

    useEffect(() => {
        if (importResponse && openImport) {
            setDisableButton(true);
            NotificationManager.success(
                messages['settingsDriver_error_started'] || 'Import started successfully',
                messages['tags_import_title'] || 'Tags Import',
                3000,
                null,
                null,
                'success'
            );
            handleClose();
        }
    }, [importResponse, openImport, messages, handleClose]);

    useEffect(() => {
        if (error) {
            setErrorMessage({
                message: error,
                type: 'error'
            });
            setDisableButton(false);
        }
    }, [error]);

    const handleDelimiterChange = useCallback((selectedDelimiter) => {
        setDelimiterLocal(selectedDelimiter);
        dispatch(setDelimiterTag(selectedDelimiter.id));
    }, [dispatch]);

    const handleImportSubmit = useCallback(() => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['settingsDriver_error_required'] || 'File is required',
                type: 'error'
            });
            return false;
        }

        if (!delimiter.id) {
            setErrorMessage({
                message: messages['tags_error_delimiter'] || 'Delimiter is required',
                type: 'error'
            });
            return false;
        }

        dispatch(clearErrorTag());
        setErrorMessage({ message: '', type: '' });
        
        dispatch(setFileKeyTag(filesUpload.value[0].fileKey));
        dispatch(importTags({ 
            fileKey: filesUpload.value[0].fileKey, 
            delimiter: delimiter.id 
        }));
    }, [filesUpload.value, delimiter.id, dispatch, messages]);

    const downloadTemplate = useCallback((e) => {
        e.preventDefault();
        
        const delimiterSelected = delimiter?.id || ',';
        const csvContent = `ID${delimiterSelected}${messages['tags_template_labelColumn']}${delimiterSelected}${messages['tags_template_parentlabel']}${delimiterSelected}${messages['tags_template_colorColumn']}\n`;
        const blob = new Blob([csvContent], {
            type: 'text/csv;encoding:utf-8;charset=UTF-8',
        });
        
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', 'tags_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [delimiter, messages]);

    return (
        <Fragment>
            <Modal
                title={messages['tags_import_title'] || 'Import Tags'}
                open={openImport}
                loading={loading}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>
                        {errorMessage.message && (
                            <Alert 
                                severity={errorMessage.type} 
                                onClose={() => setErrorMessage({ message: '', type: '' })}
                            >
                                {(() => {
                                    const errorCode = errorMessage.message.includes('_') 
                                        ? errorMessage.message.split('_')[0] 
                                        : null;
                                    
                                    return errorCode && messages[`tags_import_error_${errorCode}`]
                                        ? messages[`tags_import_error_${errorCode}`]
                                        : errorMessage.message;
                                })()}
                            </Alert>
                        )}
                        
                        <Form
                            ref={formRef}
                            onValidSubmit={handleImportSubmit}
                        >
                            <div style={{ padding: 24 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Grid item sm={12}>
                                        <div style={{ marginBottom: 16, backgroundColor: '#fff3cd', padding: 16, borderRadius: 4, fontWeight: 500 }}>
                                            {messages['tags_import_instructions']}
                                        </div>
                                        <Button
                                            type="button"
                                            className="btn-blue"
                                            style={{ width: '100%', marginBottom: 16 }}
                                            onClick={downloadTemplate}
                                        >
                                            {messages['tags_download_template']}
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <Select
                                            id="delimiter"
                                            label={messages['tags_delimiter']}
                                            placeholder={messages['tags_selectDelimiter']}
                                            valueField="id"
                                            textField="name"
                                            options={delimiters}
                                            value={delimiter}
                                            required
                                            onChange={handleDelimiterChange}
                                        />
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <FileUpload
                                            {...filesUpload}
                                            label={`${messages['settingsDriver_file'] || 'File'} (CSV)`}
                                            placeholder={messages['mapLandmarkImport_chooseFile'] || 'Choose file'}
                                            types=".csv"
                                            helpText={messages['mapLandmarkImport_formatSupport'] || 'Only CSV format supported'}
                                            maxSize={5}
                                            disabled={filesUpload?.value?.length > 0 || !delimiter?.id}
                                            maxSizeMessage={`${messages['max'] || 'Max'}: 5MB`}
                                        />
                                        <small className="form-text text-muted">
                                            {messages['mapLandmarkImport_maxSizeFile'] || 'Maximum file size: 5MB'}
                                        </small>
                                    </Grid>
                                </Grid>
                            </div>
                        </Form>
                    </div>
                </ModalBody>
                
                <ModalFooter style={{ bottom: 50, textAlign: 'right' }}>
                    <Button
                        onClick={handleClose}
                        disabled={disableButton}
                        className="btn-cancel"
                    >
                        {messages['tags_import_cancel'] || 'Cancel'}
                    </Button>

                    <Button
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formRef.current.submit();
                        }}
                        disabled={disableButton || filesUpload?.value?.length === 0 || !delimiter?.id}
                        className="btn-blue"
                    >
                        {messages['tags_import_button'] || 'Import'}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ModalImportTags;